import { useState } from "react";
import { useCookies } from "react-cookie";
import type { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import {
  authenticationErrorMessageMap,
  AuthenticationErrors,
  useAuthentication,
} from "@lib/authentication";

export const TOTP_WRONG =
  "That code did not work. Check your authenticator app and try the newest code.";
export const TOTP_EXPIRED =
  "The sign-in timed out before the code arrived. Enter your password again.";

export const useLoginForm = (onSuccess: () => void) => {
  const navigate = useNavigate();
  const { signIn, confirmTotpCode, resetPassword } = useAuthentication();
  const [, setCookie] = useCookies(["rememberMe"]);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const { t } = useTranslation();
  const params = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Password accepted, waiting for the authenticator-app code (OMG-2777).
  const [needsTotp, setNeedsTotp] = useState(false);

  const login = async ({ email, password, rememberMe }: FieldValues) => {
    try {
      setIsSubmitting(true);
      setCookie("rememberMe", rememberMe ? "true" : "false");
      if ((await signIn({ email: email, password })) === "totp") {
        setErrors({});
        return setNeedsTotp(true);
      }
      return onSuccess();
    } catch (error) {
      const errorEventProps = { ...params, error };

      if (error instanceof Error) {
        if (
          error.message === AuthenticationErrors.INVALID_CREDENTIALS ||
          error.message === AuthenticationErrors.LOCKED_ACCOUNT
        ) {
          const errorMessage = authenticationErrorMessageMap[error.message];
          return setErrors((prev) => ({
            ...prev,
            email: {
              type: "custom",
              message: t(errorMessage),
            },
          }));
        }
        errorEventProps.error = error.message;
      }

      setErrors((prev) => ({
        ...prev,
        submit: t("js.checkout.serverError"),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const forgotPassword = async ({ email }: FieldValues) => {
    try {
      setIsSubmitting(true);
      const normalizedEmail = email.toLowerCase();
      // Cognito is the source of truth for accounts now, and resetPassword
      // validates the email itself (unknown -> UserNotFound / silent per the
      // pool's user-existence-error setting), so the old verifyUser email-exists
      // pre-check (legacy GraphQL) is redundant and was dropped with OMG-2092.
      await resetPassword(normalizedEmail);
      return navigate(
        `/reset-password?email=${encodeURIComponent(normalizedEmail)}`,
        {
          state: { showResetPasswordEmailNotification: true },
        },
      );
    } catch (error) {
      const errorProps = { ...params, error };

      if (error instanceof Error) {
        errorProps.error = error.message;
      }

      setErrors((prev) => ({
        ...prev,
        submit: t("js.checkout.serverError"),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitTotp = async ({ totpCode }: FieldValues) => {
    try {
      setIsSubmitting(true);
      await confirmTotpCode(String(totpCode ?? "").replace(/\s/g, ""));
      return onSuccess();
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === AuthenticationErrors.INVALID_CODE
      ) {
        return setErrors({ totpCode: { type: "custom", message: TOTP_WRONG } });
      }
      // Cognito drops the half-finished sign-in after a few minutes; start over.
      setNeedsTotp(false);
      setErrors({ submit: { type: "custom", message: TOTP_EXPIRED } });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Returns true when the stored credentials need a code, so the caller shows
  // the form (the stored handoff cannot carry one).
  const loginByCode = async (credentials: string[]) => {
    if (credentials.length === 2) {
      if (
        (await signIn({ email: credentials[0], password: credentials[1] })) ===
        "totp"
      ) {
        setNeedsTotp(true);
        return true;
      }
      onSuccess();
    }
    return false;
  };

  return {
    loginByCode,
    login,
    submitTotp,
    needsTotp,
    errors,
    isSubmitting,
    forgotPassword,
  };
};
